import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '@/components/primitives';
import { useAuth } from '@/lib/auth';
import { useScope, ROLE_OPTIONS } from '@/lib/scope';
import { useOnboardingState } from '@/lib/onboarding';
import { getOnboardingFor } from '@/content/onboarding';
import { useViewport } from '@/lib/viewport';

export function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role, setRole } = useScope();
  const { complete, update } = useOnboardingState();
  const { isMobile } = useViewport();

  const content = useMemo(() => getOnboardingFor(role), [role]);
  const [stepIdx, setStepIdx] = useState(0);
  const step = content.steps[stepIdx] ?? content.steps[0]!;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === content.steps.length - 1;

  const firstName = (user?.displayName || user?.email || 'bạn').split(' ').slice(-1)[0];

  function next() {
    if (isLast) {
      complete(role);
      if (step.ctaHref) {
        navigate(step.ctaHref);
      } else {
        navigate('/');
      }
      return;
    }
    setStepIdx(stepIdx + 1);
  }

  function prev() {
    if (isFirst) return;
    setStepIdx(stepIdx - 1);
  }

  function skip() {
    complete(role);
    navigate('/');
  }

  function openCta() {
    if (!step.ctaHref) return;
    update({ lastSeenRole: role });
    navigate(step.ctaHref);
  }

  return (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--bx-page-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 12 : 24,
      }}
    >
      <Card
        style={{ width: '100%', maxWidth: 620 }}
        padding={isMobile ? 18 : 28}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: 'var(--bx-primary)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
              }}
            >
              {stepIdx + 1}
            </span>
            <span>{step.title}</span>
          </div>
        }
        subtitle={`Bước ${stepIdx + 1}/${content.steps.length} · ${content.roleLabel} · Xin chào ${firstName}`}
        actions={
          <Button variant="ghost" size="sm" onClick={skip} title="Bỏ qua hướng dẫn">
            Bỏ qua
          </Button>
        }
      >
        {isFirst && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              background: 'var(--bx-surface-2)',
              borderRadius: 'var(--bx-radius-md)',
              fontSize: 12.5,
              color: 'var(--bx-text-body)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="shield" size={13} style={{ color: 'var(--bx-primary)' }} />
              <span style={{ fontWeight: 600 }}>Vai trò của bạn</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => {
                    setRole(r.value);
                    setStepIdx(0);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border:
                      r.value === role
                        ? '1px solid var(--bx-primary)'
                        : '1px solid var(--bx-border)',
                    background: r.value === role ? 'var(--bx-primary)' : '#fff',
                    color: r.value === role ? '#fff' : 'var(--bx-text-body)',
                    fontSize: 12.5,
                    cursor: 'pointer',
                    fontWeight: r.value === role ? 600 : 500,
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--bx-text-muted)' }}>{content.tagline}</div>
          </div>
        )}

        <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--bx-text-body)' }}>
          {step.body}
        </div>

        {step.bullets && step.bullets.length > 0 && (
          <ul style={{ marginTop: 12, paddingLeft: 20, color: 'var(--bx-text-body)' }}>
            {step.bullets.map((b, i) => (
              <li key={i} style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                {b}
              </li>
            ))}
          </ul>
        )}

        {step.ctaHref && step.ctaLabel && (
          <div style={{ marginTop: 16 }}>
            <Button variant="ghost-primary" size="sm" iconRight="external" onClick={openCta}>
              {step.ctaLabel}
            </Button>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 22,
            paddingTop: 16,
            borderTop: '1px solid var(--bx-border-muted)',
          }}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            {content.steps.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === stepIdx ? 24 : 8,
                  height: 8,
                  borderRadius: 999,
                  background:
                    i <= stepIdx ? 'var(--bx-primary)' : 'var(--bx-border)',
                  transition: 'width 160ms',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <Button variant="outline" size="sm" icon="chevron-left" onClick={prev}>
                Quay lại
              </Button>
            )}
            <Button variant="primary" size="sm" iconRight="chevron-right" onClick={next}>
              {isLast ? 'Hoàn thành' : 'Tiếp theo'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
